import UAParser from 'ua-parser-js'

import { getEnvironmentAtRunTime } from '@cash-web/shared-util-environment'

import { webUrlToLaunchLinkHandler } from './web-url-to-launch-link'

export const ANDROID_STORE =
  'https://play.google.com/store/apps/details?id=com.squareup.cash&referrer=utm_medium%3Dweb%26utm_source%3Dcash-web'
export const APPLE_STORE = 'https://itunes.apple.com/us/app/square-cash/id711923939?pt=302818&mt=8&ct=cash-web'

// exported for testing
const APPSFLYER_CLICK_DOMAIN = 'https://click.cash.app'
const APPSFLYER_PRODUCTION_ONELINK_ID = 'ui6m'
const APPSFLYER_STAGING_ONELINK_ID = 'LhNg'
const APP_DOWNLOAD_WITH_DEEP_LINK_PRODUCTION_SHORTLINK = 'b6ca51f0'
const APP_DOWNLOAD_WITH_DEEP_LINK_STAGING_SHORTLINK = '35a5f3f8'

export const APP_DOWNLOAD_WITH_DEEP_LINK_PRODUCTION = `${APPSFLYER_CLICK_DOMAIN}/${APPSFLYER_PRODUCTION_ONELINK_ID}/${APP_DOWNLOAD_WITH_DEEP_LINK_PRODUCTION_SHORTLINK}`
const APP_DOWNLOAD_WITH_DEEP_LINK_STAGING = `${APPSFLYER_CLICK_DOMAIN}/${APPSFLYER_STAGING_ONELINK_ID}/${APP_DOWNLOAD_WITH_DEEP_LINK_STAGING_SHORTLINK}`

export type AppDownloadWithDeepLinkOptions = {
  baseUrl?: string
  shortlink?: string
  deepLinkValue?: string
  appFallbackUrl?: string
  webFallbackUrl?: string
}

export const getAppStoreUrl = () => {
  const uaParser = new UAParser()
  const os = uaParser.getOS()
  return os.name === 'Android' ? ANDROID_STORE : APPLE_STORE
}

export const getAppDownloadWithDeepLinkURL = (options?: AppDownloadWithDeepLinkOptions) => {
  const env = getEnvironmentAtRunTime()
  const isProd = env === 'production'

  const currentHref = typeof window !== 'undefined' ? window.location.href : ''
  const defaultDeepLinkValue = isProd ? currentHref : currentHref.replace(/^https?:\/\//i, 'cashme://')
  const defaultOneLinkId = isProd ? APPSFLYER_PRODUCTION_ONELINK_ID : APPSFLYER_STAGING_ONELINK_ID
  const defaultShortlink = isProd
    ? APP_DOWNLOAD_WITH_DEEP_LINK_PRODUCTION_SHORTLINK
    : APP_DOWNLOAD_WITH_DEEP_LINK_STAGING_SHORTLINK
  const defaultBaseUrl = isProd ? APP_DOWNLOAD_WITH_DEEP_LINK_PRODUCTION : APP_DOWNLOAD_WITH_DEEP_LINK_STAGING
  const configuredBaseUrl =
    options?.shortlink && options.shortlink !== defaultShortlink
      ? `${APPSFLYER_CLICK_DOMAIN}/${defaultOneLinkId}/${options.shortlink}`
      : defaultBaseUrl
  const baseUrl = options?.baseUrl || configuredBaseUrl
  const deepLinkValue = options?.deepLinkValue || defaultDeepLinkValue

  const url = new URL(baseUrl)
  url.searchParams.set('deep_link_value', deepLinkValue)

  if (options?.appFallbackUrl) {
    url.searchParams.set('af_dp', options.appFallbackUrl)
  }

  if (options?.webFallbackUrl) {
    url.searchParams.set('af_web_dp', options.webFallbackUrl)
  }

  return url.href
}

const getLaunchLinkUrl = (currentUrl?: URL) => {
  for (const routeAction of webUrlToLaunchLinkHandler) {
    const match = routeAction.match.find(routeRegexMatch => {
      const redirectToParam = currentUrl?.searchParams.get('redirectTo') ?? ''

      const redirectParam = currentUrl?.searchParams.get('redirect') ?? ''
      return (
        routeRegexMatch.test(redirectToParam) ||
        routeRegexMatch.test(decodeURIComponent(redirectParam)) ||
        routeRegexMatch.test(decodeURIComponent(currentUrl?.href ?? ''))
      )
    })

    if (match) {
      return routeAction.generateLaunchLink(currentUrl)
    }
  }
  return undefined
}

const APPSFLYER_FLOW_URL = 'https://click.cash.app/ui6m/flow'

export const getAppDownloadUrlForPath = (deepLinkPath?: `/${string}`) => {
  const url = new URL(APPSFLYER_FLOW_URL)
  if (deepLinkPath) {
    url.searchParams.set('deep_link_value', `https://cash.app${deepLinkPath}`)
  }
  return url.href
}

export const getCashmeDeepLink = (path: `/${string}`) => {
  const isProd = getEnvironmentAtRunTime() === 'production'
  const hostname = isProd ? 'cash.app' : 'cashstaging.app'
  return `cashme://${hostname}${path}`
}

/**
 * Attempts to deep link to the Cash App mobile application by redirecting the browser
 * to a custom cashme:// protocol URL that will launch the app with the appropriate context.
 *
 * The function analyzes the current web URL and tries to match it against known route patterns
 * to generate an appropriate launch link. If no specific match is found, it falls back to
 * a generic app launch URL.
 *
 * @param options.preserveParams - Array of query parameter names to preserve from the current
 *   URL and append to the launch link. This is useful for maintaining context when transitioning
 *   from web to mobile app. For example, passing ['params'] will preserve a 'params' query
 *   parameter if it exists in the current URL.
 *
 * @example
 * deeplinkToApp({ preserveParams: ['params'] })
 */
export const deeplinkToApp = (options?: { preserveParams?: string[] }) => {
  const currentUrl = typeof window !== 'undefined' ? new URL(window.location.href) : undefined
  const launchLink = getLaunchLinkUrl(currentUrl)
  const isProd = getEnvironmentAtRunTime() === 'production'
  const hostname = isProd ? 'cash.app' : 'cashstaging.app'

  // Create the base destination URL with custom path. If a path is not found, use the generic launch path.
  const finalUrl = new URL(`cashme://${hostname}${launchLink || '/launch'}`)

  // If preserveParams is specified and we have a launch link, append preserved query parameters.
  if (options?.preserveParams && launchLink && currentUrl) {
    options.preserveParams.forEach(paramName => {
      const paramValue = currentUrl.searchParams.get(paramName)
      if (paramValue) {
        finalUrl.searchParams.set(paramName, paramValue)
      }
    })
  }

  window.location.href = finalUrl.href
}
